/**
 * <worlds-parallax>
 *
 * Drives the "next world glides up and covers the previous one" reveal. CSS
 * `position: sticky` pins each world at the top of the viewport once it arrives
 * (a free, cheap hold, no JS needed for that part). This controller layers a
 * transform on top of that so the glide itself travels over more scroll distance
 * than one viewport height and eases toward its target with a lerp, instead of
 * snapping 1:1 with the scroll wheel like raw sticky motion does.
 *
 * Markup:
 *   .wp-world-track   tall spacer (padding-bottom: N vh) — gives the glide/hold
 *                      extra scroll room. Its own rect is never transformed, so
 *                      it's a reliable, un-clamped scroll-progress signal (unlike
 *                      the sticky child inside it, whose rect clamps once stuck).
 *     .wp-world        position: sticky; top: 0 — the pinned layer this
 *                      controller transforms.
 *
 * data-reveal on .wp-world-track: extra glide distance, in vh. 0/absent = static
 *   (used for the first world, which has nothing to glide over).
 * data-ease on <worlds-parallax>: lerp factor 0.02–0.5 (lower = slower/floatier).
 *
 * Independently of the glide, this controller also flags each .wp-world with an
 * `is-stuck` class once it reaches its pinned/final position (top of viewport).
 * The section uses that to fade in a full-viewport backdrop that hides the worlds
 * behind it — only once the reveal has settled, never mid-glide.
 */
class WorldsParallax extends HTMLElement {
  constructor() {
    super();
    this._items = [];
    this._worlds = [];
    this._running = false;
    this._raf = null;
    this._onScrollResize = this._onScrollResize.bind(this);
    this._tick = this._tick.bind(this);
    this._onReducedChange = this._onReducedChange.bind(this);
  }

  connectedCallback() {
    this._ease = this._clamp(this._toNumber(this.dataset.ease, 0.08), 0.02, 0.5);

    this._reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    this._reduced.addEventListener
      ? this._reduced.addEventListener('change', this._onReducedChange)
      : this._reduced.addListener(this._onReducedChange);
    if (this._reduced.matches) return;

    this._collect();
    if (!this._items.length && !this._worlds.length) return;

    this._io = new IntersectionObserver((entries) => {
      const visible = entries.some((entry) => entry.isIntersecting);
      if (visible) this._start();
      else this._stop();
    }, { threshold: 0, rootMargin: '20% 0px' });
    this._io.observe(this);
  }

  disconnectedCallback() {
    this._stop();
    if (this._io) this._io.disconnect();
    if (this._reduced) {
      this._reduced.removeEventListener
        ? this._reduced.removeEventListener('change', this._onReducedChange)
        : this._reduced.removeListener(this._onReducedChange);
    }
  }

  _onReducedChange() {
    if (this._reduced.matches) {
      this._stop();
      this._items.forEach((item) => { item.world.style.transform = ''; });
      this._worlds.forEach((world) => world.classList.remove('is-stuck'));
    } else if (this._items.length || this._worlds.length) {
      this._start();
    }
  }

  _collect() {
    this._worlds = Array.from(this.querySelectorAll('.wp-world'));

    const tracks = Array.from(this.querySelectorAll('.wp-world-track'));
    const vh = window.innerHeight || 1;

    tracks.forEach((track) => {
      const world = track.querySelector(':scope > .wp-world');
      if (!world) return;
      const revealVh = this._toNumber(track.dataset.reveal, 0);
      if (!revealVh) return; // first world / no glide configured — leave static

      const startPx = (revealVh / 100) * vh;
      world.style.willChange = 'transform';
      world.style.transform = 'translate3d(0,' + startPx + 'px,0)';
      this._items.push({ track, world, revealVh, y: startPx });
    });
  }

  _start() {
    if (this._running || (!this._items.length && !this._worlds.length)) return;
    this._running = true;
    window.addEventListener('scroll', this._onScrollResize, { passive: true });
    window.addEventListener('resize', this._onScrollResize, { passive: true });
    this._raf = requestAnimationFrame(this._tick);
  }

  _stop() {
    this._running = false;
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = null;
    window.removeEventListener('scroll', this._onScrollResize);
    window.removeEventListener('resize', this._onScrollResize);
  }

  _onScrollResize() { /* progress is read per-frame in _tick */ }

  _tick() {
    if (!this._running) return;
    const vh = window.innerHeight || 1;

    for (let i = 0; i < this._items.length; i++) {
      const item = this._items[i];
      const revealPx = (item.revealVh / 100) * vh;
      const rect = item.track.getBoundingClientRect(); // track is never transformed — reliable signal
      const total = vh + revealPx || 1;
      const entered = vh - rect.top; // 0 while the track's top sits at the viewport bottom, grows while scrolling up
      const progress = this._clamp(entered / total, 0, 1);

      const target = (1 - progress) * revealPx;
      item.y += (target - item.y) * this._ease;
      const y = Math.round(item.y * 100) / 100;
      item.world.style.transform = y ? 'translate3d(0,' + y + 'px,0)' : '';
    }

    // A world is at its pinned/final state once its top reaches the viewport top
    // (sticky top: 0). Flag it so the section can fade in its backdrop, hiding the
    // worlds behind it once the reveal has settled.
    for (let i = 0; i < this._worlds.length; i++) {
      const world = this._worlds[i];
      const stuck = world.getBoundingClientRect().top <= 1;
      if (stuck !== world.classList.contains('is-stuck')) {
        world.classList.toggle('is-stuck', stuck);
      }
    }

    this._raf = requestAnimationFrame(this._tick);
  }

  _toNumber(value, fallback) {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : fallback;
  }

  _clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }
}

if (!customElements.get('worlds-parallax')) {
  customElements.define('worlds-parallax', WorldsParallax);
}
